import {Component, OnInit, OnDestroy, ChangeDetectionStrategy, inject} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {SeoService} from '@/app/core/seo/seo.service';
import {JsonLdService} from '@/app/core/seo/json-ld.service';
import {buildBreadcrumbJsonLd} from '@/app/core/seo/article.jsonld';
import {MemberService} from '@services/member.service';
import {ActivatedRoute, Router} from '@angular/router';
import {Member, MemberRole} from '@/app/core/models/member';
import {environment} from '@/environments/environment';
import {TitleComponent} from '@/app/shared/components/title/title.component';
import {NgStyle} from '@angular/common';
import {determineRoleIcon} from '../../core/utils/memberUtils';
import {AchievementRowComponent} from '@components/achievement-row/achievement-row.component';
import {MemberCardComponent} from '@components/member-card/member-card.component';
import {Title} from '@angular/platform-browser';
import {TranslatePipe} from '@ngx-translate/core';
import {LastVideosComponent} from '@components/last-videos/last-videos.component';
import {OverwatchLogoComponent} from '@/app/shared/components/logo/overwatch-logo/overwatch-logo.component';
import {MarvelRivalsLogoComponent} from '@/app/shared/components/logo/marvel-rivals-logo/marvel-rivals-logo.component';
import {TranslateDynamicService} from '@services/translate-dynamic.service';
import {RosterTypeList} from '@/app/core/models/rosterType';

@Component({
	selector: 'app-member-details',
	imports: [
		NgStyle,
		AchievementRowComponent,
		TitleComponent,
		MemberCardComponent,
		TranslatePipe,
		LastVideosComponent,
		OverwatchLogoComponent,
		MarvelRivalsLogoComponent,

	],
	templateUrl: './member-details.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./member-details.component.css']
})
export class MemberDetailsComponent implements OnInit, OnDestroy {

	private static readonly JSON_LD_BREADCRUMB = 'member-breadcrumb';

	private readonly seo = inject(SeoService);
	private readonly jsonLd = inject(JsonLdService);
	private readonly translate = inject(TranslateService);

	constructor(
		private readonly memberService: MemberService,
		private readonly activateRoute: ActivatedRoute,
		private readonly router: Router,
		private readonly titleService: Title,
		protected readonly translateDynamicService: TranslateDynamicService
	) {}

	protected readonly determineRoleIcon = determineRoleIcon;

	member: Member | null = null;
	minioBaseUrl = environment.minioBaseUrl;

	members: Member[] = [];

	ngOnInit(): void {
		this.activateRoute.params.subscribe(params => {
			const memberId = params['memberId'];
			this.loadMember(memberId);
		})
    }

	ngOnDestroy(): void {
		this.jsonLd.remove(MemberDetailsComponent.JSON_LD_BREADCRUMB);
	}

	loadMember(memberId: string): void {
		this.memberService.getMemberById(memberId).subscribe({
			next: (member) => {
				this.member = member;
				this.titleService.setTitle('Team Peps - ' + member.pseudo.toString());
				this.publishSeo(member);
				this.loadRoster();
			},
			error: (error) => {
				console.error('Error fetching member details:', error);
				this.router.navigate(['/not-found']);
			}
		})
	}

	loadRoster(): void {
		this.memberService.getActiveMembers(this.member!.rosterType).subscribe({
			next: (members) => {
				this.members = [...members['members'], ...members['substitutes'], ...members['coaches']];
			},
			error: (error) => {
				console.error('Error fetching roster:', error);
			}
		})
	}

	protected readonly MemberRole = MemberRole;
	protected readonly RosterTypeList = RosterTypeList;

	/** Balises et fil d'Ariane de la fiche joueur. */
	private publishSeo(member: Member): void {
		const lang = this.translateDynamicService.getLang();
		const path = `/player/${member.id}`;
		const roleAndTeam = [member.pseudo, member.role, 'Team Peps'].filter(Boolean).join(' · ');
		const description = member.translations?.[lang]?.description?.trim()
			|| `${roleAndTeam}. ${this.translate.instant('page.member-details.seo-description')}`;

		this.seo.setPage({
			title: member.pseudo,
			description,
			path,
			image: this.seo.mediaUrl(member.imageKey),
			type: 'profile',
		});

		this.jsonLd.set(MemberDetailsComponent.JSON_LD_BREADCRUMB, buildBreadcrumbJsonLd([
			{ name: 'Team Peps', url: this.seo.absoluteUrl('/') },
			{ name: member.pseudo, url: this.seo.absoluteUrl(path) },
		]));
	}
}
